import { useEmbeddedPaymentBlock } from "@/hooks/use-embedded-payment-block";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clock, CreditCard, QrCode, Copy, Check, Loader2, ArrowLeft, Wallet, Sparkles } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { authAxios } from "@/lib/axios";
import { useMyDataQuery, useMyCpmWalletQuery, useRedeemWalletForExtraMinutesMutation } from "@/generated/graphql";
import { computeExtraMinutesPrice } from "@/lib/billing/computeExtraMinutesPrice";
import { computeWalletApplication } from "@/lib/billing/computeWalletApplication";
import { resolveRedeemOutcome, RedeemPixPayload } from "@/lib/billing/resolveRedeemOutcome";
import { isSubscriptionPastDue } from "@/lib/billing/isSubscriptionPastDue";
import { isSubscriptionPastDueError } from "@/lib/quota/isSubscriptionPastDueError";
import { formatCents } from "@/lib/format/currency";
import { CancelPendingHoldButton } from "@/components/wallet/CancelPendingHoldButton";
import { isPhoneInvalidError } from "@/lib/errors/isPhoneInvalidError";

const MINIMUM_MINUTES = 60;

interface BuyExtraMinutesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Step = 'select' | 'pix-form' | 'pix-payment' | 'wallet-confirm' | 'success';

const formatCPFCNPJ = (value: string) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return digits
    .slice(0, 14)
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
};

const BuyExtraMinutesModalContent = ({ open, onOpenChange }: BuyExtraMinutesModalProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { data: myData, refetch } = useMyDataQuery();
  const isPastDue = isSubscriptionPastDue(myData?.myData?.subscription);
  const { data: walletData, refetch: refetchWallet } = useMyCpmWalletQuery();
  const [redeemWalletForExtraMinutes] = useRedeemWalletForExtraMinutesMutation();

  const [minutes, setMinutes] = useState(60);
  const [step, setStep] = useState<Step>('select');
  const [isLoading, setIsLoading] = useState(false);
  // When true, the pix-form/pix-payment steps belong to a wallet redemption:
  // the PIX charge is only the remainder, created by the mutation (hold placed
  // server-side) instead of the plain REST order.
  const [walletMode, setWalletMode] = useState(false);
  const [documentNumber, setDocumentNumber] = useState('');
  const [pixData, setPixData] = useState<{
    id: string;
    amount: number;
    status: string;
    pix_code?: string | null;
    pix_qr_code_url?: string | null;
    expires_at?: string | null;
  } | null>(null);
  const pixPollingRef = useRef<NodeJS.Timeout | null>(null);

  const totalPrice = computeExtraMinutesPrice(minutes);
  const walletAvailableCents = walletData?.myCpmWallet?.available_cents ?? 0;
  const canUseWallet = walletAvailableCents > 0;
  const isRedemptionHoldPending = walletData?.myCpmWallet?.pending_kind === 'plan_redemption'
    || walletData?.myCpmWallet?.pending_kind === 'minutes_redemption';
  const walletApplication = computeWalletApplication({
    availableCents: walletAvailableCents,
    priceCents: Math.round(totalPrice * 100),
  });
  // What the user still pays now (the PIX/card charge) — full price on the
  // normal path, only the remainder on a wallet redemption.
  const payNowValue = walletMode ? walletApplication.remainingCents / 100 : totalPrice;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  const getTimeDisplay = () => {
    if (hours > 0 && remainingMinutes > 0) {
      return t('account.buyExtraMinutesHours', { hours, minutes: remainingMinutes });
    }
    if (hours > 0) {
      return t('account.buyExtraMinutesHoursOnly', { hours });
    }
    return t('account.buyExtraMinutesMinutesOnly', { minutes: remainingMinutes });
  };

  const pastDueToast = () => {
    toast({
      title: t('account.buyExtraMinutesPastDue'),
      variant: "destructive",
    });
  };

  const handleStripeCheckout = async () => {
    if (isPastDue) {
      pastDueToast();
      return;
    }
    setIsLoading(true);
    try {
      const response = await authAxios.post('/api/subscriptions/extra-minutes/stripe', {
        minutes,
        returnUrl: window.location.href,
        cancelUrl: window.location.href,
      });
      window.location.href = response.data.url;
    } catch (error: any) {
      console.error('Error creating Stripe checkout:', error);
      toast({
        title: isSubscriptionPastDueError(error)
          ? t('account.buyExtraMinutesPastDue')
          : t('account.buyExtraMinutesError'),
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const walletErrorToast = (error: unknown) => {
    if (isSubscriptionPastDueError(error) || isPastDue) {
      pastDueToast();
      return;
    }
    if (isPhoneInvalidError(error)) {
      toast({
        title: t('account.buyExtraMinutesError'),
        description: t('checkout.invalidPhoneDesc'),
        variant: 'destructive',
      });
      return;
    }
    const message = typeof error === 'string' ? error : (error as { message?: string })?.message || '';
    const known: Record<string, string> = {
      wallet_balance_too_low: t('checkout.walletErrorBalanceTooLow'),
      payout_request_already_pending: t('checkout.walletErrorPayoutPending'),
      remainder_method_required: t('checkout.walletErrorRemainderRequired'),
    };
    const match = Object.keys(known).find((code) => message.includes(code));
    toast({
      title: t('account.buyExtraMinutesError'),
      description: match ? known[match] : undefined,
      variant: "destructive",
    });
  };

  // Full coverage: the wallet pays everything — debits instantly, no provider.
  const handleWalletFullConfirm = async () => {
    if (isPastDue) {
      pastDueToast();
      return;
    }
    setIsLoading(true);
    try {
      const { data } = await redeemWalletForExtraMinutes({
        variables: { minutes },
      });
      const outcome = resolveRedeemOutcome(data?.redeemWalletForExtraMinutes);
      if (outcome.action === 'completed') {
        setStep('success');
        toast({ title: t('account.buyExtraMinutesSuccess') });
        await Promise.all([refetch(), refetchWallet()]);
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        walletErrorToast('');
      }
    } catch (error: any) {
      console.error('Error redeeming wallet for minutes:', error);
      walletErrorToast(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Partial, card remainder: hold placed server-side, Stripe checkout for the rest.
  const handleWalletCard = async () => {
    if (isPastDue) {
      pastDueToast();
      return;
    }
    setIsLoading(true);
    try {
      const { data } = await redeemWalletForExtraMinutes({
        variables: {
          minutes,
          remainderMethod: 'credit_card',
          returnUrl: window.location.href,
        },
      });
      const outcome = resolveRedeemOutcome(data?.redeemWalletForExtraMinutes);
      if (outcome.action === 'redirect') {
        window.location.href = outcome.url;
        return;
      }
      walletErrorToast('');
      setIsLoading(false);
    } catch (error: any) {
      console.error('Error redeeming wallet for minutes:', error);
      walletErrorToast(error);
      setIsLoading(false);
    }
  };

  const handlePixPayment = async () => {
    if (isPastDue) {
      pastDueToast();
      return;
    }
    const cleanDoc = documentNumber.replace(/\D/g, '');
    if (cleanDoc.length !== 11 && cleanDoc.length !== 14) {
      return;
    }

    setIsLoading(true);
    try {
      let pixPayload: RedeemPixPayload;
      if (walletMode) {
        // Partial, PIX remainder: the mutation places the hold and returns the
        // remainder order (same last-transaction shape as the REST endpoint).
        const { data } = await redeemWalletForExtraMinutes({
          variables: {
            minutes,
            remainderMethod: 'pix',
            document: {
              number: cleanDoc,
              type: cleanDoc.length === 11 ? 'cpf' : 'cnpj',
            },
          },
        });
        const outcome = resolveRedeemOutcome(data?.redeemWalletForExtraMinutes);
        if (outcome.action !== 'pix') {
          walletErrorToast('');
          return;
        }
        pixPayload = outcome.pix;
      } else {
        const response = await authAxios.post('/api/subscriptions/extra-minutes/pix', {
          minutes,
          documentNumber: cleanDoc,
          documentType: cleanDoc.length === 11 ? 'cpf' : 'cnpj',
        });
        pixPayload = response.data;
      }

      setPixData({
        id: String(pixPayload.id),
        amount: payNowValue,
        status: 'pending',
        pix_code: pixPayload.qr_code,
        pix_qr_code_url: pixPayload.qr_code_url,
        expires_at: pixPayload.expires_at,
      });
      setStep('pix-payment');
      startPixPolling(String(pixPayload.id));
    } catch (error: any) {
      console.error('Error creating PIX order:', error);
      if (isSubscriptionPastDueError(error) || isPastDue) {
        pastDueToast();
      } else if (isPhoneInvalidError(error)) {
        toast({
          title: t('account.buyExtraMinutesError'),
          description: t('checkout.invalidPhoneDesc'),
          variant: 'destructive',
        });
      } else if (walletMode) {
        walletErrorToast(error);
      } else {
        toast({
          title: t('account.buyExtraMinutesError'),
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const startPixPolling = (orderId: string) => {
    if (pixPollingRef.current) {
      clearInterval(pixPollingRef.current);
    }

    let attempts = 0;
    const maxAttempts = 120;

    pixPollingRef.current = setInterval(async () => {
      attempts++;
      if (attempts >= maxAttempts) {
        stopPixPolling();
        return;
      }

      try {
        const response = await authAxios.get(`/api/subscriptions/pix/${orderId}`);
        const status = response.data?.status;

        if (status === 'paid') {
          stopPixPolling();
          setStep('success');
          toast({
            title: t('account.buyExtraMinutesSuccess'),
          });
          // Wallet redemptions also debit the held balance on confirmation.
          await Promise.all([refetch(), refetchWallet()]);
          setTimeout(() => {
            handleClose();
          }, 2000);
        } else if (status === 'expired' || status === 'canceled' || status === 'failed') {
          stopPixPolling();
          setPixData(prev => prev ? { ...prev, status } : null);
        }
      } catch (error) {
        console.error('Error polling PIX status:', error);
      }
    }, 5000);
  };

  const stopPixPolling = () => {
    if (pixPollingRef.current) {
      clearInterval(pixPollingRef.current);
      pixPollingRef.current = null;
    }
  };

  const handleClose = () => {
    stopPixPolling();
    onOpenChange(false);
    setStep('select');
    setMinutes(60);
    setDocumentNumber('');
    setPixData(null);
    setWalletMode(false);
    setIsLoading(false);
  };

  const copyPixCode = async () => {
    if (pixData?.pix_code) {
      try {
        await navigator.clipboard.writeText(pixData.pix_code);
        toast({ title: t('account.buyExtraMinutesCodeCopied') });
      } catch (err) {
        console.error('Failed to copy PIX code:', err);
      }
    }
  };

  const formatPixExpiration = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  useEffect(() => {
    if (!open) {
      stopPixPolling();
    }
    return () => {
      stopPixPolling();
    };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            {t('account.buyExtraMinutesTitle')}
          </DialogTitle>
        </DialogHeader>

        {step === 'success' ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-500" />
            </div>
            <p className="text-lg font-semibold text-green-500">
              {t('account.buyExtraMinutesSuccess')}
            </p>
          </div>
        ) : step === 'pix-payment' && pixData ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              {t('account.buyExtraMinutesPixDescription')}
            </p>

            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                {walletMode ? t('account.buyExtraMinutesWalletRemainder') : t('account.buyExtraMinutesTotal')}
              </p>
              <p className="text-2xl font-bold text-primary">
                {payNowValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </p>
              {walletMode && (
                <p className="text-xs text-lime-600 dark:text-lime-400 font-medium">
                  {t('account.buyExtraMinutesWalletApplied')}: − {formatCents(walletApplication.appliedCents, 'BRL')}
                </p>
              )}
              <p className="text-sm text-muted-foreground mt-1">
                {minutes} min = {getTimeDisplay()}
              </p>
            </div>

            {pixData.pix_qr_code_url && (
              <div className="flex justify-center">
                <div className="bg-white p-3 rounded-lg">
                  <img
                    src={pixData.pix_qr_code_url}
                    alt="QR Code PIX"
                    className="w-48 h-48"
                  />
                </div>
              </div>
            )}

            {pixData.pix_code && (
              <div className="space-y-2">
                <div className="bg-muted p-3 rounded-lg">
                  <p className="text-xs font-mono text-muted-foreground break-all line-clamp-3">
                    {pixData.pix_code}
                  </p>
                </div>
                <Button variant="outline" className="w-full" onClick={copyPixCode}>
                  <Copy className="w-4 h-4 mr-2" />
                  {t('account.buyExtraMinutesCopyCode')}
                </Button>
              </div>
            )}

            {pixData.expires_at && (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Clock className="w-4 h-4" />
                <span>
                  {t('account.buyExtraMinutesPixExpires')}: {formatPixExpiration(pixData.expires_at)}
                </span>
              </div>
            )}

            <div className="flex items-center justify-center gap-2 text-sm text-primary">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>{t('account.buyExtraMinutesPixWaiting')}</span>
            </div>
          </div>
        ) : step === 'pix-form' ? (
          <div className="space-y-4">
            <Button
              variant="ghost"
              size="sm"
              className="p-0 h-auto text-muted-foreground"
              onClick={() => {
                setWalletMode(false);
                setStep('select');
              }}
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              {t('account.buyExtraMinutesBack')}
            </Button>

            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                {walletMode ? t('account.buyExtraMinutesWalletRemainder') : t('account.buyExtraMinutesTotal')}
              </p>
              <p className="text-2xl font-bold text-primary">
                {payNowValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </p>
              {walletMode && (
                <p className="text-xs text-lime-600 dark:text-lime-400 font-medium">
                  {t('account.buyExtraMinutesWalletApplied')}: − {formatCents(walletApplication.appliedCents, 'BRL')}
                </p>
              )}
              <p className="text-sm text-muted-foreground mt-1">
                {minutes} min = {getTimeDisplay()}
              </p>
            </div>

            <div className="space-y-2">
              <Label>{t('account.buyExtraMinutesCpfCnpj')}</Label>
              <Input
                placeholder="000.000.000-00"
                value={documentNumber}
                onChange={(e) => setDocumentNumber(formatCPFCNPJ(e.target.value))}
                maxLength={18}
              />
            </div>

            <Button
              className="w-full"
              onClick={handlePixPayment}
              disabled={isLoading || documentNumber.replace(/\D/g, '').length < 11}
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <QrCode className="w-4 h-4 mr-2" />
              )}
              {t('account.buyExtraMinutesGeneratePix')}
            </Button>
          </div>
        ) : step === 'wallet-confirm' ? (
          <div className="space-y-4">
            <Button
              variant="ghost"
              size="sm"
              className="p-0 h-auto text-muted-foreground"
              onClick={() => setStep('select')}
              disabled={isLoading}
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              {t('account.buyExtraMinutesBack')}
            </Button>

            <div className="text-center">
              <p className="text-lg font-semibold">{getTimeDisplay()}</p>
              <p className="text-sm text-muted-foreground">{minutes} min</p>
            </div>

            <div className="p-4 rounded-lg bg-lime-500/10 border border-lime-500/30 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('account.buyExtraMinutesTotal')}</span>
                <span className="font-semibold">
                  {totalPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('checkout.walletBalanceApplied')}</span>
                <span className="font-semibold text-lime-600 dark:text-lime-400">
                  − {formatCents(walletApplication.appliedCents, 'BRL')}
                </span>
              </div>
              <p className="text-sm font-semibold text-center pt-1 border-t border-lime-500/20">
                {t('checkout.walletFullCoverage')}
              </p>
            </div>

            <Button
              className="w-full bg-lime-400 hover:bg-lime-500 text-lime-950 font-semibold"
              onClick={handleWalletFullConfirm}
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              {t('checkout.walletConfirmButton')}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('account.buyExtraMinutesDescription')}
            </p>

            <div className="space-y-2">
              <Label>{t('account.buyExtraMinutesLabel')}</Label>
              <Input
                type="number"
                min={1}
                step={1}
                value={minutes}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val > 0) {
                    setMinutes(val);
                  }
                }}
              />
              <p className="text-xs text-muted-foreground">
                {t('account.buyExtraMinutesMinimum')} &middot; {t('account.buyExtraMinutesPricePerMinute')}
              </p>
            </div>

            <div className="bg-muted/50 rounded-lg p-4 flex flex-col items-center gap-1">
              <p className="text-lg font-semibold">{getTimeDisplay()}</p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{t('account.buyExtraMinutesTotal')}</span>
                <span className="text-xl font-bold text-primary">
                  {totalPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </span>
              </div>
            </div>

            {!canUseWallet && isRedemptionHoldPending && (
              <div className="p-3 rounded-lg border border-amber-500/40 bg-amber-500/5 space-y-2">
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  {t('wallet.pendingHoldNotice')}
                </p>
                <CancelPendingHoldButton
                  className="w-full"
                  onCancelled={async () => {
                    await refetchWallet();
                  }}
                />
              </div>
            )}

            {canUseWallet && (
              <div className="p-4 rounded-lg bg-lime-500/10 border border-lime-500/30 space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-lime-600 dark:text-lime-400">
                  <Wallet className="w-4 h-4" />
                  {t('account.buyExtraMinutesWalletTitle')}
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('account.buyExtraMinutesWalletAvailable')}</span>
                  <span className="font-medium">{formatCents(walletAvailableCents, 'BRL')}</span>
                </div>
                {walletApplication.coversFully ? (
                  <Button
                    className="w-full bg-lime-400 hover:bg-lime-500 text-lime-950 font-semibold"
                    onClick={() => setStep('wallet-confirm')}
                    disabled={isLoading || minutes < MINIMUM_MINUTES}
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {t('account.buyExtraMinutesWalletPayFull')}
                  </Button>
                ) : (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('account.buyExtraMinutesWalletRemainder')}</span>
                      <span className="font-medium">{formatCents(walletApplication.remainingCents, 'BRL')}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        className="bg-lime-400 hover:bg-lime-500 text-lime-950 font-semibold"
                        onClick={handleWalletCard}
                        disabled={isLoading || minutes < MINIMUM_MINUTES}
                      >
                        {isLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <CreditCard className="w-4 h-4 mr-2" />
                        )}
                        {t('account.buyExtraMinutesWalletPlusCard')}
                      </Button>
                      <Button
                        variant="outline"
                        className="border-lime-500/40 hover:bg-lime-500/10"
                        onClick={() => {
                          setWalletMode(true);
                          setStep('pix-form');
                        }}
                        disabled={isLoading || minutes < MINIMUM_MINUTES}
                      >
                        <QrCode className="w-4 h-4 mr-2" />
                        {t('account.buyExtraMinutesWalletPlusPix')}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground text-center">
                      {t('checkout.walletHoldNote')}
                    </p>
                  </>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="default"
                onClick={handleStripeCheckout}
                disabled={isLoading || minutes < MINIMUM_MINUTES}
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                {t('account.buyExtraMinutesPayCard')}
              </Button>
              <Button
                variant="outline"
                onClick={() => setStep('pix-form')}
                disabled={isLoading || minutes < MINIMUM_MINUTES}
              >
                <QrCode className="w-4 h-4 mr-2" />
                {t('account.buyExtraMinutesPayPix')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const BuyExtraMinutesModal = (props: React.ComponentProps<typeof BuyExtraMinutesModalContent>) => {
  const embedded = useEmbeddedPaymentBlock(props.open, props.onOpenChange);
  return embedded ? null : <BuyExtraMinutesModalContent {...props} />;
};

export default BuyExtraMinutesModal;
